/**
 * What: Shopify-side scope and structure check for the insights answer Web publishes.
 * Transport: Web (strict, source of truth) -> Shopify worker (this check) -> merchant browser (tolerant render parse).
 * Rule: wording, arithmetic and card-grammar checks belong to Web's strict schema, not here. Types derive from that schema.
 */
import * as z from 'zod';
import { storeDataContextSchema } from './store-data-context.js';
const answer = z.looseObject({
    state: z.string(),
    evidence: z.looseObject({ context: storeDataContextSchema }).optional(),
});
const composition = z.looseObject({
    page: z.looseObject({ sections: z.array(z.looseObject({ id: z.string(), kind: z.string() })) }),
});
const shared = { answers: z.record(z.string(), answer), composition };
/** Loose objects keep every field Web sent, so the response reaches the browser intact. */
export const insightsDeliverySchema = z.discriminatedUnion('state', [
    z.looseObject({ state: z.literal('ready'), context: storeDataContextSchema, ...shared }),
    z.looseObject({ state: z.literal('unavailable'), reason: z.string(), ...shared }),
]);
/** Expected source lifecycle, separate from the unchanged successful analytical answer. */
const correlationId = z.string().min(1).max(128).nullable();
export const insightsReadinessSchema = z.discriminatedUnion('state', [
    z.object({
        state: z.literal('not_started'),
        error: z.literal('INSIGHTS_NOT_STARTED'),
        reason: z.enum(['settings_missing', 'source_missing']),
        retryable: z.literal(false),
        retryAfterMs: z.null(),
        correlationId,
    }),
    z.object({
        state: z.literal('preparing'),
        error: z.literal('INSIGHTS_PREPARING'),
        reason: z.enum(['settings_sync', 'source_sync', 'state_changed']),
        retryable: z.literal(true),
        retryAfterMs: z.literal(30_000),
        correlationId,
    }),
    z.object({
        state: z.literal('blocked'),
        error: z.literal('INSIGHTS_BLOCKED'),
        reason: z.enum(['privacy', 'access', 'writer_unsettled']),
        retryable: z.literal(false),
        retryAfterMs: z.null(),
        correlationId,
    }),
    z.object({
        state: z.literal('failed'),
        error: z.literal('INSIGHTS_FAILED'),
        reason: z.enum(['settings_sync_failed', 'source_sync_failed']),
        retryable: z.literal(false),
        retryAfterMs: z.null(),
        correlationId,
    }),
]);
const readinessCodes = {
    not_started: 'INSIGHTS_NOT_STARTED',
    preparing: 'INSIGHTS_PREPARING',
    blocked: 'INSIGHTS_BLOCKED',
    failed: 'INSIGHTS_FAILED',
};
/** Plain errors and pure factories follow the shared contract-package boundary. */
export function createInsightsReadinessError(input, id) {
    const body = insightsReadinessSchema.parse({
        ...input,
        error: readinessCodes[input.state],
        retryable: input.state === 'preparing',
        retryAfterMs: input.state === 'preparing' ? 30_000 : null,
        correlationId: id ?? null,
    });
    let status = 409;
    if (body.state === 'failed')
        status = 500;
    if (body.state === 'blocked')
        status = body.reason === 'access' ? 403 : 503;
    return Object.assign(new Error(body.error), { name: 'InsightsReadinessError', body, status });
}
export function isInsightsReadinessError(error) {
    return error instanceof Error && error.name === 'InsightsReadinessError' && 'body' in error && 'status' in error;
}
//# sourceMappingURL=insights-delivery.js.map